<template>
  <div class="h-screen flex flex-col">
    <!-- Barra superior fixa -->
    <header class="bg-white shadow-md z-50 h-16 flex items-center justify-between px-4 relative">
      <div class="flex items-center">
        <!-- Botão para mostrar menu lateral em mobile -->
        <button @click="mobileMenuOpen = true" class="mr-3 lg:hidden text-gray-500 focus:outline-none">
          <Bars3BottomLeftIcon class="h-6 w-6" aria-hidden="true" />
          <span class="absolute -top-1 -right-1">
            <ChatBadge v-if="!currentIsChats && showUnseenChatsBadge" />
          </span>
        </button>

        <!-- Logo -->
        <router-link :to="{ name: 'index' }" class=" items-center shrink-0">
          <img class="hidden sm:flex h-10 w-auto" src="/imgs/wbuy_ai_logo.png" alt="wBuy.ai logo" />
        </router-link>
      </div>



      <!-- Avatar e menu de usuário no lado direito -->
      <div class="flex gap-4 items-center">

        <!-- Mostrar créditos apenas se não tiver agentes cadastrados -->

          <div class="flex gap-1 items-center pl-4 pr-2 justify-center border border-gray-200 rounded-full transition-all duration-300 cursor-pointer hover:border-gray-400 hover:shadow-sm"
               :class="{
                 'animate-pulse-once': creditChanged
               }"
               @click="$router.push('/settings/credits')"
               title="Ver extrato de créditos">

            <div class="flex flex-col items-start justify-center pr-2 pt-1">
              <div class="leading-none -mb-0.5" style="font-size: 11px;">
                Créditos
              </div>
              <div class="flex items-center gap-1">
                <transition
                  name="credit-change"
                  mode="out-in"
                >
                  <div :key="animationKey" class="text-xl font-bold" :class="{
                    'text-green-600': parseFloat(credits) > 100,
                    'text-orange-500': parseFloat(credits) > 0 && parseFloat(credits) <= 100,
                    'text-red-600': parseFloat(credits) <= 0
                  }">
                    {{ parseFloat(credits).toFixed(2) || '0.00' }}
                  </div>
                </transition>
                <!-- Botão de reload dos créditos -->
                <button
                  @click="reloadCredits"
                  class="p-1 rounded-full hover:bg-white/50 transition-all duration-200 group"
                  :class="{ 'animate-spin': reloadingCredits }"
                  :disabled="reloadingCredits"
                  title="Atualizar créditos"
                >
                  <svg class="w-3.5 h-3.5 transition-colors" :class="{
                    'text-green-600 group-hover:text-green-700': parseFloat(credits) > 100,
                    'text-orange-500 group-hover:text-orange-600': parseFloat(credits) > 0 && parseFloat(credits) <= 100,
                    'text-red-500 group-hover:text-red-600': parseFloat(credits) <= 0
                  }" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                  </svg>
                </button>
              </div>
            </div>

        </div>


        <Menu as="div" class="relative z-[9999]">
          <MenuButton class="flex items-center focus:outline-none relative">
            <Avatar :avatar="user.avatar" size='md' class="cursor-pointer" />
            <!-- Indicador de status do Socket -->
            <div
              class="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white"
              :class="socketConnected ? 'bg-green-500' : 'bg-red-500'"
              :title="socketConnected ? 'Conectado' : 'Desconectado'"
            ></div>
          </MenuButton>

          <Teleport to="body">
            <transition enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95">
              <MenuItems
                class="absolute right-4 top-16 w-48 rounded-lg shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-[9999]">
                <MenuItem v-slot="{ active }">
                <div @click="openMySettings"
                  :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 cursor-pointer']">
                  Configurações
                </div>
                </MenuItem>
                <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                <div @click="item.click"
                  :class="[active ? 'bg-gray-100' : '', 'block px-4 py-2 text-sm text-gray-700 cursor-pointer']">
                  {{ item.name }}
                </div>
                </MenuItem>
              </MenuItems>
            </transition>
          </Teleport>
        </Menu>


        
      </div>
    </header>

    <!-- Faixas de aviso (ex.: WhatsApp desconectado) -->
    <NoticeBanner />

    <!-- Conteúdo principal: barra lateral + área de conteúdo -->
    <div class="flex flex-1 overflow-hidden ">
      <!-- Barra lateral fixa estreita (visível apenas em desktop) -->
      <aside
        class="hidden lg:flex flex-col bg-white border-r border-gray-200 overflow-y-auto overflow-x-hidden absolute left-0 top-16 bottom-0 z-40 w-56"
        ref="rightSideContainer">

        <div class="flex-1 py-4 flex flex-col space-y-1">
          <!-- Home Icon -->
          <div class="pb-0.5 w-full">
            <router-link :to="{ name: 'index' }" title="Home" class="w-full">
              <div
                class="flex items-center h-10 rounded-lg mx-3 pl-[10px] transition-colors duration-200"
                :class="isCurrentRoute('/') && $route.path === '/' ? 'bg-[#2a2a2a] text-white' : 'text-[#5c5c5c] hover:bg-[#f5f5f5]'"
              >
                <HomeIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <span
                  v-show="sidebarExpanded"
                  class="ml-3 text-sm font-medium whitespace-nowrap"
                >
                  Home
                </span>
              </div>
            </router-link>
          </div>
          <div v-for="item in filteredSidebarNavigation" :key="item.routePath" class="pb-0.5 w-full">
            <div v-if="item.routePath == '/tickets'" class="w-full">
              <router-link :to="routerTicket()" :title="item.name" :alt="item.name" class="w-full">
                <div :class="[
                  item.current ? 'bg-rose-700 text-white' : 'text-gray-100 hover:bg-slate-300',
                  'flex items-center h-10 rounded-lg mx-3 pl-[10px] transition-colors duration-200'
                ]">
                  <EnvelopeIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  <span v-show="sidebarExpanded" class="ml-3 text-sm font-medium whitespace-nowrap">{{ item.name }}</span>
                </div>
              </router-link>
            </div>
            <div v-else-if="item.routePath == '/chats'" class="w-full">
              <router-link :to="routerChat()" :title="item.name" :alt="item.name" class="w-full">
                <div :class="[
                  item.current ? 'bg-rose-700 text-white' : 'text-gray-700 hover:bg-slate-300',
                  'flex items-center h-10 rounded-lg mx-3 pl-[10px] transition-colors duration-200 relative'
                ]">
                  <component :is="item.icon" class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  <span v-show="sidebarExpanded" class="ml-3 text-sm font-medium whitespace-nowrap">{{ item.name }}</span>
                  <div class="absolute right-2">
                    <ChatBadge v-if="!currentIsChats && showUnseenChatsBadge" />
                  </div>
                </div>
              </router-link>
            </div>
            <div v-else-if="item.routePath !== '/settings'" class="w-full">
              <router-link :to="item.routePath" :title="item.name" :alt="item.name" class="w-full">
                <div :class="[
                  item.current ? 'bg-[#2a2a2a] text-white' : 'text-[#5c5c5c] hover:bg-[#f5f5f5]',
                  'flex items-center h-10 rounded-lg mx-3 pl-[10px] transition-colors duration-200'
                ]">
                  <component :is="item.icon" class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                  <span v-show="sidebarExpanded" class="ml-3 text-sm font-medium whitespace-nowrap">{{ item.name }}</span>
                </div>
              </router-link>
            </div>
          </div>
        </div>

        

        <!-- Admin link -->
        <div class="pb-6 flex flex-col border-t border-gray-100 pt-4">
          <div v-if="managemnt" class="mb-2 w-full">
            <router-link :to="{ name: 'managemnt' }" class="w-full">
              <div class="flex items-center h-10 rounded-lg mx-3 pl-[10px] transition-colors duration-200 text-gray-400 hover:bg-[#f5f5f5] hover:text-gray-600">
                <KeyIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <span v-show="sidebarExpanded" class="ml-3 text-sm font-medium whitespace-nowrap">Administração</span>
              </div>
            </router-link>
          </div>

          <!-- Settings icon -->
          <div v-if="hasSettingsAccess" class="w-full">
            <router-link :to="{ name: 'settings' }" :title="'Settings'" alt="Settings" class="w-full">
              <div
                class="flex items-center h-10 rounded-lg mx-3 pl-[10px] transition-colors duration-200"
                :class="isCurrentRoute('/settings') ? 'bg-[#2a2a2a] text-white' : 'text-gray-400 hover:bg-[#f5f5f5] hover:text-gray-600'"
              >
                <Cog6ToothIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                <span v-show="sidebarExpanded" class="ml-3 text-sm font-medium whitespace-nowrap">Configurações</span>
              </div>
            </router-link>
          </div>
        </div>
      </aside>

      <!-- Spacer para compensar o menu lateral absoluto -->
      <div class="hidden lg:block w-56 flex-shrink-0"></div>


      <!-- Menu mobile (slide-in) -->
      <TransitionRoot as="template" :show="mobileMenuOpen">
        <Dialog as="div" class="lg:hidden relative z-50" @close="mobileMenuOpen = false">
          <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0"
            enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
            leave-to="opacity-0">
            <div class="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </TransitionChild>

          <div class="fixed inset-0 flex z-40">
            <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
              enter-from="-translate-x-full" enter-to="translate-x-0"
              leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
              leave-to="-translate-x-full">
              <DialogPanel class="relative flex-1 flex flex-col max-w-xs w-full bg-white">
                <TransitionChild as="template" enter="ease-in-out duration-300" enter-from="opacity-0"
                  enter-to="opacity-100" leave="ease-in-out duration-300" leave-from="opacity-100" leave-to="opacity-0">
                  <div class="absolute top-0 right-0 -mr-12 pt-2">
                    <button type="button"
                      class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      @click="mobileMenuOpen = false">
                      <span class="sr-only">Close sidebar</span>
                      <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </TransitionChild>

                <div class="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
                  <div class="flex-shrink-0 flex items-center px-4">
                    <img class="h-8 w-auto" src="/imgs/wbuy_ai_logo.png" alt="wBuy.ai" />
                  </div>
                  <nav class="mt-5 px-2 space-y-1">
                    <div v-for="item in filteredSidebarNavigation" :key="item.name" :to="item.routePath" :title="item.name"
                      :alt="item.name" :class="[
                        item.current ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100 hover:text-slate-800',
                        'group py-2 px-3 rounded-md flex items-center text-sm font-medium cursor-pointer'
                      ]" :aria-current="item.current ? 'page' : undefined" @click="openRoute(item.routePath)">
                      <component :is="item.icon" :class="[
                        item.current ? 'text-white' : 'text-gray-700 group-hover:text-gray-500',
                        'mr-3 h-5 w-5'
                      ]" aria-hidden="true" />
                      <span>{{ item.name }}</span>
                      <ChatBadge class="ml-2"
                        v-if="item.routePath == '/chats' && !currentIsChats && showUnseenChatsBadge" />
                    </div>
                  </nav>
                </div>

                <!-- Admin link in mobile menu -->
                <div class="flex-shrink-0 flex border-t border-stone-200 p-4" v-if="managemnt">
                  <router-link :to="{ name: 'managemnt' }" class="flex items-center">
                    <KeyIcon class="h-6 w-6 text-gray-400 mr-3" aria-hidden="true" />
                    <div class="text-sm">Administração</div>
                  </router-link>
                </div>
              </DialogPanel>
            </TransitionChild>
            <div class="flex-shrink-0 w-14">
              <!-- Force sidebar to shrink to fit close icon -->


            </div>
          </div>
        </Dialog>
      </TransitionRoot>

      <!-- Área de conteúdo principal -->
      <main class="flex-1 flex flex-col overflow-auto bg-stone-100">
        <router-view />
      </main>
    </div>


    <!-- Avisos do admin ao lojista (popup obrigatorio com "Li e entendi") -->
    <NoticeGate />

    <!-- Configurações pessoais -->
    <Slider :open="mySettings" title="Minhas configurações" :formClose="openMySettings">
      <mySettingsForm :formClose="openMySettings" :reloadTemplate="reloadTemplate" />
    </Slider>
  </div>
</template>

<script>
import mySettingsForm from '_app/views/settings/mySettingsForm.vue';
import { Dialog, DialogOverlay, DialogPanel, Menu, MenuButton, MenuItem, MenuItems, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { Teleport } from 'vue'
import { HomeIcon, CalendarIcon, PresentationChartBarIcon, PuzzlePieceIcon, BeakerIcon, AcademicCapIcon, Bars3BottomLeftIcon, Squares2X2Icon, MagnifyingGlassIcon, XMarkIcon, ChatBubbleBottomCenterTextIcon, LifebuoyIcon, Cog6ToothIcon, FlagIcon, QueueListIcon, ArrowLeftOnRectangleIcon, KeyIcon, EnvelopeIcon, CpuChipIcon } from "@heroicons/vue/24/outline";
import { usewbuy_aiStore } from '_app/stores/wbuy_aiStore';
import ChatBadge from '_app/resources/components/chats/ChatBadge.vue';
import NoticeGate from '_app/resources/components/notices/NoticeGate.vue';
import NoticeBanner from '_app/resources/components/notices/NoticeBanner.vue';
import PlanService from '_root/logic/services/PlanService';

export default {
  components: {
    mySettingsForm,
    Dialog,
    DialogPanel,
    DialogOverlay,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    HomeIcon,
    EnvelopeIcon,
    TransitionRoot,
    CalendarIcon,
    AcademicCapIcon,
    Bars3BottomLeftIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    ChatBubbleBottomCenterTextIcon,
    LifebuoyIcon,
    Cog6ToothIcon,
    FlagIcon,
    QueueListIcon,
    ArrowLeftOnRectangleIcon,
    Squares2X2Icon,
    KeyIcon,
    ChatBadge,
    NoticeGate,
    NoticeBanner,
    PresentationChartBarIcon,
    BeakerIcon,
    PuzzlePieceIcon,
  },

  data() {
    return {
      userData: this.$SessionService.getData(),
      assistantIsOpen: false,
      reload: false,
      sidebarNavigation: [],
      userNavigation: [
        { name: 'Logout', href: '/auth/logout', click: this.logout },
      ],
      mobileMenuOpen: false,
      dropMenu: false,
      managemnt: false,
      mySettings: false,
      wbuy_aiStore: usewbuy_aiStore(),
      creditChanged: false,
      animationKey: 0,
      reloadingCredits: false,
      socketConnected: false,
      sidebarExpanded: true,
      // Rotas que não aparecem no menu lateral (funcionalidade ainda não implementada)
      hiddenMenuRoutes: ['tools:calendar'],
      // Mapeamento de nomes do menu (de-para)
      menuNameMapping: {
        'Agents': 'Concierge',
        'agents': 'Concierge',
        'Studio': 'Studio de Moda',
        'studio': 'Studio de Moda',
        'Calendar': 'Agendamento',
        'calendar': 'Agendamento',
        'Calendário': 'Agendamento',
        'Funil': 'Esteira de Vendas',
        'funil': 'Esteira de Vendas',
        'Funnels': 'Esteira de Vendas',
        'funnels': 'Esteira de Vendas',
        'Crm': 'Contatos',
        'crm': 'Contatos',
        'Home': 'Home',
      },
    }
  },

  computed: {
    currentIsChats() {
      const current = this.sidebarNavigation.find((n) => n.current);
      return current?.routeName === 'chats';
    },

    modalClasses() {
      return {
        'w-[95%] h-full': true,
        'md:w-[60%] md:h-auto': true,
      };
    },

    hasSettingsAccess() {
      return this.sidebarNavigation.some(item => item.routePath === '/settings');
    },

    showUnseenChatsBadge() {
      //return this.notificationStore.getterNotificationChatTeamCount + this.notificationStore.getterNotificationChatNetworkCount;
    },
    
    credits() {
      // Garantir que o valor retornado seja um número com precision correta
      const value = this.wbuy_aiStore.getCredits();
      return typeof value === 'number' ? value : parseFloat(value) || 0;
    },

    isLocalhost() {
      return window.location.hostname.includes("localhost");
    },

    filteredSidebarNavigation() {
      return this.sidebarNavigation;
    },
    

  },

  methods: {
    createGradient() {
      const startColor = '#ffffff';
      const endColor = '#ffffff';

      if (this.$refs.rightSideContainer) {
        this.$refs.rightSideContainer.style.background =
          `linear-gradient(to top right, ${startColor}, ${endColor})`;
      }
    },

    openAssistantModal() {
      this.assistantIsOpen = true;
    },

    closeAssistantModal() {
      this.assistantIsOpen = false;
    },

    openRoute(routePath) {
      this.$router.push(routePath);
      this.mobileMenuOpen = false;
    },

    removeNotification(notification) {
      //this.chatNetworkStore.removeNotificationInvite(notification);
    },

    isCurrentRoute(routePath) {
      if (this.$route.path.startsWith(routePath)) {
        return true;
      }
      return false;
    },

    openMenu() {
      this.dropMenu = true;
    },

    closeMenu() {
      this.dropMenu = false;
    },

    openMySettings() {
      this.dropMenu = false;
      this.mySettings = !this.mySettings;

      if (this.reload) {
        this.reload = false;
        window.location.reload();
      }
    },

    reloadTemplate() {
      this.reload = true;
    },

    async logout() {
      localStorage.removeItem('data');
      this.$ApiService.post(`/auth/logout`).then(
        (response) => {
          // Handle response
        }
      );
      window.location.href = `/auth/logout/${this.user.id}`;
    },

    reloadCredits() {
      if (this.reloadingCredits) return;

      this.reloadingCredits = true;

      // Emite evento via socket para solicitar atualização dos créditos
      if (this.$socket && this.$socket.connected) {
        this.$socket.emit('credits:refresh');
      }

      // Aguarda um pouco para mostrar a animação
      setTimeout(() => {
        this.reloadingCredits = false;
      }, 600);
    },
  },

  watch: {
    $route(newRoute, oldRoute) {
      for (let i = 0; i < this.sidebarNavigation.length; i++) {
        if (this.isCurrentRoute(this.sidebarNavigation[i].routePath)) {
          this.sidebarNavigation[i].current = true;
        }
        else {
          this.sidebarNavigation[i].current = false;
        }
      }
    },

    credits(newCredits, oldCredits) {
      if (newCredits !== oldCredits) {
        this.creditChanged = true;
        this.animationKey += 1;
        setTimeout(() => {
          this.creditChanged = false;
        }, 1000);
      }
    },
  },

  async created() {
    this.$ApiService.clearCache();
    this.user = this.$SessionService.getUser();
    this.managemnt = this.$SessionService.getOwner().managemnt || false;

    const routes = this.$RouteService.getRoutes();
    this.sidebarNavigation = routes.filter((route) => !this.hiddenMenuRoutes.includes(route.routeName)).map((route) => {
      // Corrigir path do CRM para ir para a página principal
      let routePath = route.path;
      if (route.routeName === 'crm' || route.path === '/crm/contacts') {
        routePath = '/crm';
      }
      // Aplicar mapeamento de nomes
      const mappedName = this.menuNameMapping[route.name] || this.menuNameMapping[route.routeName] || route.name;
      return {
        name: mappedName,
        routePath: routePath,
        routeName: route.routeName,
        icon: route.icon,
        current: this.isCurrentRoute(route.path),
      };
    });

    // Adicionar Studio se não existir
    const hasStudio = this.sidebarNavigation.find(item => item.routePath === '/studio');
    if (!hasStudio) {
      this.sidebarNavigation.splice(1, 0, {
        name: 'Studio de Moda',
        routePath: '/studio',
        routeName: 'studio',
        icon: 'iconStudio',
        current: this.isCurrentRoute('/studio'),
      });
    }

    // Adicionar Provador Virtual se não existir
    const hasVirtualTryOn = this.sidebarNavigation.find(item => item.routePath === '/virtual-tryon');
    if (!hasVirtualTryOn) {
      // Encontrar posição após o Studio
      const studioIndex = this.sidebarNavigation.findIndex(item => item.routePath === '/studio');
      const insertIndex = studioIndex !== -1 ? studioIndex + 1 : 2;
      this.sidebarNavigation.splice(insertIndex, 0, {
        name: 'Provador Virtual',
        routePath: '/virtual-tryon',
        routeName: 'virtual-tryon',
        icon: 'iconVirtualTryOn',
        current: this.isCurrentRoute('/virtual-tryon'),
      });
    }

    // Adicionar Carrinho Abandonado se não existir
    const hasCarrinho = this.sidebarNavigation.find(item => item.routePath === '/carrinho');
    if (!hasCarrinho) {
      const virtualTryOnIndex = this.sidebarNavigation.findIndex(item => item.routePath === '/virtual-tryon');
      const insertIndex = virtualTryOnIndex !== -1 ? virtualTryOnIndex + 1 : 3;
      this.sidebarNavigation.splice(insertIndex, 0, {
        name: 'Carrinho Abandonado',
        routePath: '/carrinho',
        routeName: 'carrinho',
        icon: 'iconCarrinho',
        current: this.isCurrentRoute('/carrinho'),
      });
    }

    await this.$SocketService.connect(this.$socket);

    // Listeners do socket
    this.$socket.on('connect', () => {
      this.socketConnected = true;
    });

    this.$socket.on('connect_error', () => {
      this.socketConnected = false;
    });

    this.$socket.on('disconnect', () => {
      this.socketConnected = false;
    });

    // Listeners de reconexão
    this.$socket.io.on('reconnect', () => {
      this.socketConnected = true;
    });

    this.$socket.io.on('reconnect_failed', () => {
      this.socketConnected = false;
    });

    this.wbuy_aiStore.initializeListeners(this.$socket);

    // Verificar status inicial
    this.socketConnected = this.$socket.connected;

    this.$nextTick(() => {
      this.createGradient();
    });
  },
}
</script>

<style scoped>
/* Transições suaves no modal */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.5s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

@keyframes pulse-once {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
}

.animate-pulse-once {
  animation: pulse-once 1s ease-in-out;
}

/* Animação para mudança de créditos */
.credit-change-enter-active,
.credit-change-leave-active {
  transition: all 0.3s ease;
}

.credit-change-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.credit-change-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>